/**
 * Bidirectional conversion between the app's markdown dialect (what Tahiti's
 * `content` column stores) and a TipTap/ProseMirror JSON document — the core
 * of the Point 7 editor migration.
 *
 * Parsing block structure and inline spans (bold/italic/strike/code/links,
 * headings, hr, fenced code, lists, blockquotes) is delegated to `marked`'s
 * lexer (already a dependency, used for view-mode rendering before this
 * migration) rather than hand-rolled regex — it already implements the
 * CommonMark-ish grammar this app's content was always written against.
 *
 * `docToMarkdown` is written as the deliberate inverse of `markdownToDoc`, so
 * loading a word's content and saving it back WITHOUT editing reproduces the
 * original string byte-for-byte (verified by tests/markdown-tiptap.test.ts —
 * this round-trip fidelity was the explicit gate before wiring this module
 * into word-detail.tsx: an editor rewrite must never silently reformat a
 * word's stored content on the next unrelated save).
 */
import { isExternalTarget, parseWordLinkId } from '@/lib/word-links';
import { marked } from 'marked';

// ── marked token shapes we actually consume (marked doesn't export these as types) ──

interface InlineToken {
  type: string;
  raw: string;
  text?: string;
  href?: string;
  tokens?: InlineToken[];
}

interface BlockToken {
  type: string;
  raw: string;
  text?: string;
  depth?: number;
  lang?: string;
  ordered?: boolean;
  start?: number | '';
  tokens?: BlockToken[] | InlineToken[];
  items?: BlockToken[];
}

/** A minimal ProseMirror/TipTap JSON node — looser than `@tiptap/core`'s own type, but sufficient here. */
export interface DocNode {
  type: string;
  attrs?: Record<string, unknown>;
  content?: DocNode[];
  text?: string;
  marks?: { type: string; attrs?: Record<string, unknown> }[];
}

// ── markdown → TipTap doc ────────────────────────────────────────────────────

/** Convert marked inline tokens into TipTap inline content (text + hardBreak nodes, with marks). */
function inlineTokensToContent(tokens: InlineToken[] | undefined): DocNode[] {
  if (!tokens) return [];
  const out: DocNode[] = [];

  const pushText = (text: string, marks: DocNode['marks'] = []) => {
    // A literal embedded newline (soft break within a markdown paragraph) becomes a hardBreak node.
    const parts = text.split('\n');
    parts.forEach((part, i) => {
      if (i > 0) out.push({ type: 'hardBreak' });
      if (part)
        out.push(marks.length ? { type: 'text', text: part, marks } : { type: 'text', text: part });
    });
  };

  const withMark = (
    children: InlineToken[] | undefined,
    mark: NonNullable<DocNode['marks']>[number],
  ) => {
    for (const node of inlineTokensToContent(children)) {
      if (node.type !== 'text') {
        out.push(node);
        continue;
      }
      out.push({ ...node, marks: [...(node.marks ?? []), mark] });
    }
  };

  for (const token of tokens) {
    switch (token.type) {
      case 'text':
      case 'escape':
        pushText(token.text ?? token.raw);
        break;
      case 'br':
        out.push({ type: 'hardBreak' });
        break;
      case 'strong':
        withMark(token.tokens, { type: 'bold' });
        break;
      case 'em':
        withMark(token.tokens, { type: 'italic' });
        break;
      case 'del':
        withMark(token.tokens, { type: 'strike' });
        break;
      case 'codespan':
        pushText(token.text ?? '', [{ type: 'code' }]);
        break;
      case 'link': {
        const href = token.href ?? '';
        // `raw` preserves the original target verbatim so a malformed/legacy
        // target (a bare word, no `#id` — the rare non-conforming tail found
        // in live devdb) round-trips byte-for-byte instead of collapsing to
        // `#` on the next save; only used as the serialize fallback when `id`
        // couldn't be parsed.
        const mark = isExternalTarget(href)
          ? { type: 'link', attrs: { href } }
          : { type: 'wordLink', attrs: { id: parseWordLinkId(href), raw: href } };
        withMark(token.tokens, mark);
        break;
      }
      default:
        // Unhandled inline token (image, html, …) — fall back to its literal source
        // rather than dropping it silently.
        pushText(token.raw);
    }
  }
  return out;
}

/** Convert a list_item's block-level children into TipTap content nodes (usually a single paragraph). */
function listItemChildrenToContent(children: BlockToken[] | undefined): DocNode[] {
  if (!children || children.length === 0) return [{ type: 'paragraph' }];
  const nodes: DocNode[] = [];
  for (const child of children) {
    if (child.type === 'text') {
      // marked wraps simple (non-loose) list item content in a pseudo "text" block
      // token that itself carries the real inline tokens.
      nodes.push({
        type: 'paragraph',
        content: inlineTokensToContent(child.tokens as InlineToken[]),
      });
    } else {
      nodes.push(...blockTokenToNodes(child));
    }
  }
  return nodes.length > 0 ? nodes : [{ type: 'paragraph' }];
}

/** Convert one marked block token into 0+ TipTap block nodes. */
function blockTokenToNodes(token: BlockToken): DocNode[] {
  switch (token.type) {
    case 'space':
      return [];
    case 'heading':
      return [
        {
          type: 'heading',
          attrs: { level: token.depth ?? 1 },
          content: inlineTokensToContent(token.tokens as InlineToken[]),
        },
      ];
    case 'hr':
      return [{ type: 'horizontalRule' }];
    case 'code':
      return [
        {
          type: 'codeBlock',
          attrs: { language: token.lang || null },
          content: token.text ? [{ type: 'text', text: token.text }] : [],
        },
      ];
    case 'blockquote':
      return [
        {
          type: 'blockquote',
          content: (token.tokens as BlockToken[] | undefined)?.flatMap(blockTokenToNodes) ?? [
            { type: 'paragraph' },
          ],
        },
      ];
    case 'list':
      return [
        {
          type: token.ordered ? 'orderedList' : 'bulletList',
          ...(token.ordered && typeof token.start === 'number' && token.start !== 1
            ? { attrs: { start: token.start } }
            : {}),
          content: (token.items ?? []).map((item) => ({
            type: 'listItem',
            content: listItemChildrenToContent(item.tokens),
          })),
        },
      ];
    case 'paragraph':
      return [{ type: 'paragraph', content: inlineTokensToContent(token.tokens as InlineToken[]) }];
    default:
      // Unhandled block token (table, html, …) — degrade to a literal paragraph
      // rather than dropping content silently.
      return [{ type: 'paragraph', content: [{ type: 'text', text: token.raw }] }];
  }
}

/** Parse markdown into a TipTap/ProseMirror JSON document. Empty input yields a single empty paragraph. */
export function markdownToDoc(markdown: string): DocNode {
  const tokens = marked.lexer(markdown) as BlockToken[];
  const content = tokens.flatMap(blockTokenToNodes);
  return { type: 'doc', content: content.length > 0 ? content : [{ type: 'paragraph' }] };
}

// ── TipTap doc → markdown ─────────────────────────────────────────────────────

/** Wrap `text` in markdown syntax for each of `marks`, innermost first. Code excludes all other marks (TipTap/CommonMark convention). */
function applyMarks(text: string, marks: DocNode['marks']): string {
  if (!marks || marks.length === 0) return text;
  const has = (type: string) => marks.some((m) => m.type === type);
  if (has('code')) return `\`${text}\``;

  let result = text;
  if (has('italic')) result = `*${result}*`;
  if (has('bold')) result = `**${result}**`;
  if (has('strike')) result = `~~${result}~~`;

  const wordLink = marks.find((m) => m.type === 'wordLink');
  const link = marks.find((m) => m.type === 'link');
  if (wordLink) {
    const id = wordLink.attrs?.id;
    result = `[${result}](${id ? `#${String(id)}` : String(wordLink.attrs?.raw ?? '')})`;
  } else if (link) result = `[${result}](${String(link.attrs?.href ?? '')})`;

  return result;
}

/** Serialize a TipTap inline-content array back into markdown text. */
function inlineToMarkdown(content: DocNode[] | undefined): string {
  if (!content) return '';
  return content
    .map((node) => {
      if (node.type === 'hardBreak') return '\n';
      if (node.type === 'text') return applyMarks(node.text ?? '', node.marks);
      return '';
    })
    .join('');
}

/** Serialize one list item's content back into markdown, indenting any continuation blocks. */
function listItemToMarkdown(item: DocNode, marker: string): string {
  const blocks = (item.content ?? []).map((child) => nodeToMarkdown(child)).filter((s) => s !== '');
  if (blocks.length === 0) return marker.trimEnd();
  const indent = ' '.repeat(marker.length);
  return blocks
    .map((block, i) =>
      i === 0 ? marker + block : `${indent}${block.split('\n').join(`\n${indent}`)}`,
    )
    .join('\n');
}

/** Serialize one TipTap block node back into markdown. */
function nodeToMarkdown(node: DocNode): string {
  switch (node.type) {
    case 'paragraph':
      return inlineToMarkdown(node.content);
    case 'heading': {
      const level = Number(node.attrs?.level ?? 1);
      return `${'#'.repeat(level)} ${inlineToMarkdown(node.content)}`;
    }
    case 'horizontalRule':
      return '---';
    case 'codeBlock': {
      const lang = typeof node.attrs?.language === 'string' ? node.attrs.language : '';
      const text = node.content?.[0]?.text ?? '';
      return `\`\`\`${lang}\n${text}\n\`\`\``;
    }
    case 'blockquote':
      return (node.content ?? [])
        .map((child) => nodeToMarkdown(child))
        .filter((s) => s !== '')
        .join('\n\n')
        .split('\n')
        .map((line) => (line ? `> ${line}` : '>'))
        .join('\n');
    case 'bulletList':
      return (node.content ?? []).map((item) => listItemToMarkdown(item, '- ')).join('\n');
    case 'orderedList': {
      const start = typeof node.attrs?.start === 'number' ? node.attrs.start : 1;
      return (node.content ?? [])
        .map((item, i) => listItemToMarkdown(item, `${start + i}. `))
        .join('\n');
    }
    default:
      return '';
  }
}

/** Serialize a TipTap/ProseMirror JSON document back into markdown. Inverse of {@link markdownToDoc}. */
export function docToMarkdown(doc: DocNode): string {
  const blocks = doc.content ?? [];
  // The empty-doc default (a single empty paragraph) round-trips to an empty string.
  if (blocks.length === 1 && blocks[0]?.type === 'paragraph' && !blocks[0].content?.length) {
    return '';
  }
  return blocks
    .map((node) => nodeToMarkdown(node))
    .filter((s) => s !== '')
    .join('\n\n');
}
